/* Theme selection: follow the OS, or override it.
 *
 * The choice is written to <html data-theme> and to localStorage. "system"
 * removes the attribute entirely so the prefers-color-scheme media query in
 * ds.css takes back over — cleaner than reading the OS preference and
 * mirroring it, which would go stale when the OS switches.
 *
 * The setting lives in a tiny module-level store rather than component
 * state because two unrelated places need it: the control in the header
 * that sets it, and the heatmap, which computes its colour ramp in JS and
 * so has to know whether the page is *actually* dark. useSyncExternalStore
 * keeps both in step without threading a context through every page.
 */

import { useCallback, useSyncExternalStore } from 'react'

export type Theme = 'system' | 'light' | 'dark'

const KEY = 'millstack-theme'
export const THEMES: Theme[] = ['system', 'light', 'dark']

export const THEME_LABEL: Record<Theme, string> = {
  system: 'Auto',
  light: 'Light',
  dark: 'Dark',
}

function read(): Theme {
  try {
    const v = window.localStorage.getItem(KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    /* private mode, or storage disabled */
  }
  return 'system'
}

let current: Theme = read()
const listeners = new Set<() => void>()

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export function readTheme(): Theme { return current }

export function setTheme(theme: Theme): void {
  current = theme
  applyTheme(theme)
  try { window.localStorage.setItem(KEY, theme) } catch { /* ignore */ }
  listeners.forEach((fn) => fn())
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, readTheme, () => 'system' as Theme)
  return [theme, useCallback(setTheme, [])]
}

// --------------------------------------------------------------- resolved
const mq = () => window.matchMedia('(prefers-color-scheme: dark)')

function subscribeDark(fn: () => void): () => void {
  const m = mq()
  m.addEventListener('change', fn)
  const un = subscribe(fn)
  return () => { m.removeEventListener('change', fn); un() }
}

/** Whether the page is dark *right now* — the setting resolved against the
 *  OS. Subscribes to both the stored choice and the media query, because
 *  either can change while the page is open. */
export function useIsDark(): boolean {
  return useSyncExternalStore(
    subscribeDark,
    () => (current === 'system' ? mq().matches : current === 'dark'),
    () => false,
  )
}
